import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "myrkf.install.dismissed.v1";
const DELAY_MS = 4000;

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/** Mobile-only "Add to Home Screen" banner.
 *  - Android/Chrome: captures the native `beforeinstallprompt` event and offers a real Install button.
 *  - iOS Safari: no such event exists, so shows a lightweight instruction with the Share icon.
 *  - Dismissal is persisted in localStorage — never nags again once the user closes it.
 *  - Hidden on desktop, hidden when already installed (standalone), hidden while offline.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    // Only nag phones — the desktop sidebar already shows the app clearly.
    if (window.innerWidth >= 1024) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari never fires beforeinstallprompt — surface the manual hint anyway.
    let iosTimer;
    if (ios) {
      iosTimer = setTimeout(() => setVisible(true), DELAY_MS);
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      localStorage.setItem(DISMISS_KEY, "installed");
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [ios]);

  const dismiss = (reason = "user") => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, reason);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setVisible(false);
      if (outcome === "accepted") {
        localStorage.setItem(DISMISS_KEY, "installed");
      }
      // If dismissed, remember for this session but allow future prompts via clearing DISMISS_KEY manually.
    } catch (_) {
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-40 lg:hidden bg-[#0F172A] text-white rounded-xl shadow-2xl border border-slate-700/50 p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-label="Install My RKF"
      data-testid="install-prompt"
    >
      <div className="w-10 h-10 rounded-lg bg-white overflow-hidden flex items-center justify-center shrink-0">
        <img src="/rkf-logo.webp" alt="RKF" className="w-full h-full object-contain" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm">Install My RKF</div>
        {ios ? (
          <div className="text-xs text-slate-300 mt-1 leading-relaxed">
            Tap <Share className="inline w-3 h-3 mx-0.5 align-text-bottom" /> Share, then
            <span className="font-semibold text-white"> Add to Home Screen</span> to launch like an app.
          </div>
        ) : (
          <div className="text-xs text-slate-300 mt-1 leading-relaxed">
            Launch from your home screen — one tap, no browser bar.
          </div>
        )}
        {!ios && deferredPrompt && (
          <Button
            size="sm"
            onClick={install}
            className="mt-3 h-8 bg-white text-[#0F172A] hover:bg-slate-100 font-semibold"
            data-testid="install-prompt-install"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Install
          </Button>
        )}
      </div>
      <button
        type="button"
        onClick={() => dismiss()}
        className="w-8 h-8 rounded-md hover:bg-slate-800 flex items-center justify-center shrink-0"
        aria-label="Dismiss"
        data-testid="install-prompt-dismiss"
      >
        <X className="w-4 h-4 text-slate-400" />
      </button>
    </div>
  );
}
